/**
 * water_look — the water-realism wave's Atlas side (2026-09-25; research
 * report §11, docs/research/2026-09-25-visual-realism/water.md).
 *
 * ONE place that turns the tier's water switches (quality_tiers WATER_TIERS)
 * into the engine material's opt-in config for each body — the sea, the
 * lakes, the rivers and the FAR sea — plus the shared pieces every body
 * reads: the spectrum-baked detail normal maps (R2, baked once per session),
 * the far sea's material (R5, the far tier wears it), the planar reflection
 * budget (R15) and the underwater in-scatter the medium glows with (R12).
 * The builders are pure over a preset so `verify:water-look` pins them.
 */
import * as THREE from 'three/webgpu';
import {
  createRealisticWaterMaterial,
  DEFAULT_REALISTIC_WATER,
  type RealisticWaterHandles,
  type RealisticWaterMaterialConfig,
} from 'enginex/environment/water/RealisticWaterMaterial.js';
import { bakeSpectrumNormalMap, SPECTRUM_PRESETS, type SpectrumNormalStats } from 'enginex/environment/water/spectrumNormalMap.js';
import { createWaveSet, type GerstnerWave } from 'enginex/environment/water/GerstnerWaves.js';
import { COASTAL_SCATTERING, POPE_FRY_ABSORPTION, extinction, scatterAlbedo, transmittance } from 'enginex/environment/water/waterLook.js';
import { generateWaterNormalMap } from 'enginex/environment/WaterSystem.js';
import type { TierPreset } from './quality_tiers.js';

// ── The look for a preset ────────────────────────────────────────────────────

export interface WaterLook {
  sky: boolean;
  clouds: boolean;
  spectrum: boolean;
  scattering: boolean;
  glitter: boolean;
  farSea: boolean;
  bed: boolean;
  bedCaustics: boolean;
  medium: boolean;
  marine: boolean;
  calm: boolean;
  falls: boolean;
  budget: boolean;
  crowns: boolean;
  /** R10: the camera-centred GPU foam memory (needs the foam trails). */
  foamField: boolean;
  /** R11: the river's Manning speeds, two-phase flow and Froude white water. */
  riverFlow: boolean;
}

/** The resolved switches: a missing field reads OFF except the all-tier ones; the sky needs the full sky (a flat sky bakes no PMREM). */
export function waterLookFor(p: Partial<TierPreset>): WaterLook {
  const fullSky = p.skyQuality !== 'flat';
  const clouds = p.cloudsEnabled !== false;
  return {
    sky: (p.waterSkyReflection ?? true) && fullSky,
    clouds: (p.waterCloudReflection ?? false) && fullSky && clouds,
    spectrum: p.waterSpectrumNormals ?? true,
    scattering: p.waterScattering ?? false,
    glitter: p.waterGlitterFilter ?? true,
    farSea: (p.waterFarSea ?? false) && p.distantDetail !== false,
    bed: p.waterAnalyticBed ?? false,
    bedCaustics: p.waterBedCaustics ?? false,
    medium: p.waterUnderwaterMedium ?? false,
    marine: (p.waterMarineLife ?? false) && (p.particleCap ?? 0) > 0,
    calm: p.waterCalmLakes ?? true,
    falls: (p.waterfallFx ?? false),
    budget: (p.waterReflectionBudget ?? false) && (p.waterReflections ?? false),
    crowns: (p.waterSplashCrowns ?? false) && (p.particleCap ?? 0) > 0,
    foamField: (p.waterFoamField ?? false) && p.waterFoam === true,
    riverFlow: p.waterRiverFlow ?? false,
  };
}

/** A signature of everything that changes a water material's graph — a change rebuilds the bodies. */
export function waterLookSignature(p: Partial<TierPreset>): string {
  const l = waterLookFor(p);
  return [l.sky, l.clouds, l.spectrum, l.scattering, l.glitter, l.bed, l.calm, l.farSea, !!p.waterReflections, l.medium, l.marine, l.crowns, l.falls, l.budget, !!p.rainRipples, l.foamField, l.riverFlow]
    .map((b) => (b ? 1 : 0)).join('');
}

// ── R2: the detail normal maps (baked once per session, shared by every body) ─

export interface WaterNormalMaps {
  choppy: THREE.Texture;
  calm: THREE.Texture;
  /** The stored mean-square slope of each (the material folds it into roughness at distance). */
  choppyVariance: number;
  calmVariance: number;
  /** The bake's statistics (for the probe/harness). */
  stats: { choppy: SpectrumNormalStats | null; calm: SpectrumNormalStats | null };
  source: 'spectrum' | 'sines';
}

let spectrumMaps: WaterNormalMaps | null = null;
let sineMaps: WaterNormalMaps | null = null;
/** Patch sizes of the two maps (m per repeat): the materials' normalScale is 1 / patch. */
export const CHOPPY_PATCH_M = SPECTRUM_PRESETS.choppy.patchSize ?? 8;
export const CALM_PATCH_M = SPECTRUM_PRESETS.calm.patchSize ?? 5;

/**
 * The shared detail maps. Spectrum on (R2): two JONSWAP bakes (~20–60 ms once);
 * off: the legacy four-sine map for both. NEVER disposed by a body — the
 * session owns them (a rebuild re-uses the same textures).
 */
export function waterNormalMaps(spectrum: boolean): WaterNormalMaps {
  if (spectrum) {
    if (!spectrumMaps) {
      const c = bakeSpectrumNormalMap({ ...SPECTRUM_PRESETS.choppy, resolution: 256 });
      const k = bakeSpectrumNormalMap({ ...SPECTRUM_PRESETS.calm, resolution: 256 });
      c.texture.userData.shared = true;
      k.texture.userData.shared = true;
      spectrumMaps = { choppy: c.texture, calm: k.texture, choppyVariance: c.stats.meanSquareSlope, calmVariance: k.stats.meanSquareSlope, stats: { choppy: c.stats, calm: k.stats }, source: 'spectrum' };
    }
    return spectrumMaps;
  }
  if (!sineMaps) {
    const t = generateWaterNormalMap(256);
    t.userData.shared = true;
    sineMaps = { choppy: t, calm: t, choppyVariance: 1.4, calmVariance: 1.4, stats: { choppy: null, calm: null }, source: 'sines' };
  }
  return sineMaps;
}

/** The legacy map's detail scale (1/m) — 6.25 m per repeat. */
export const SINE_NORMAL_SCALE = 0.16;

// ── The per-body opt-in config ──────────────────────────────────────────────

export type WaterBody = 'sea' | 'lake' | 'river' | 'far';

/** Cloud reflection addressing — the cloud field's own (stage4: 10 km repeat, the lattice tilt). */
export const CLOUD_REFLECT_SCALE_M = 10_000;
export const CLOUD_REFLECT_ROTATION = Math.atan2(0.454, 0.891);
/** The mesh field's condensation level (main.ts meshField.baseAltitude 900) + half a cloud. */
export const CLOUD_REFLECT_ALTITUDE_M = 1200;

/** Scatter constants per body: the open sea glows green-turquoise, a lake is browner and dimmer, a river a touch of both. */
export const SCATTER_BY_BODY: Record<WaterBody, { color: [number, number, number]; body: number; lambert: number; crest: number }> = {
  sea: { color: [0.02, 0.16, 0.14], body: 0.5, lambert: 0.35, crest: 1.6 },
  far: { color: [0.02, 0.16, 0.14], body: 0.5, lambert: 0.35, crest: 1.6 },
  lake: { color: [0.03, 0.09, 0.06], body: 0.45, lambert: 0.3, crest: 0.4 },
  river: { color: [0.03, 0.1, 0.07], body: 0.45, lambert: 0.3, crest: 0.4 },
};

/** Calm-lake glass (R13): the glass's roughness, cat's-paw patch scale (1/m) and their downwind drift (m/s). */
export const LAKE_CALM = { roughness: 0.035, patchScale: 1 / 70, patchSpeed: 1.4, glassFloor: 0.18 };

/** Bed albedos (linear) where no bake exists: a lake's silty floor, a river's gravel. */
export const LAKE_BED_RGB = 0x5a4e3a;
export const RIVER_BED_RGB = 0x6f6048;

/**
 * The realism fields for one body under a look. `maps` are the shared detail
 * maps; `bedTexture`/`planeSize` the sea's baked bed albedo (R6).
 */
export function waterLookConfig(
  body: WaterBody,
  look: WaterLook,
  maps: WaterNormalMaps,
  opts: { bedTexture?: THREE.Texture | null; planeSize?: { x: number; z: number }; cloudTexture?: THREE.Texture | null } = {},
): Partial<RealisticWaterMaterialConfig> {
  const lakeLike = body === 'lake' || body === 'river';
  const useCalm = lakeLike && look.spectrum;
  const normalMap = useCalm ? maps.calm : maps.choppy;
  const patch = look.spectrum ? (useCalm ? CALM_PATCH_M : CHOPPY_PATCH_M) : 1 / SINE_NORMAL_SCALE;
  const variance = useCalm ? maps.calmVariance : maps.choppyVariance;
  const out: Partial<RealisticWaterMaterialConfig> = {
    normalMap,
    normalScale: 1 / patch,
    // Every body's sun-driven terms read the owner's key (lib/water_lighting `waterLight` → uSunLight) — session 3.
    physicalSun: true,
    envReflection: look.sky,
    glitterFilter: look.glitter ? { normalMapVariance: variance, detailWavelength: patch / 6, normalFadeDistance: body === 'far' ? 400 : 900 } : null,
    scattering: look.scattering ? { ...SCATTER_BY_BODY[body] } : null,
  };
  if (look.clouds) out.cloudReflection = { texture: opts.cloudTexture ?? null, scaleM: CLOUD_REFLECT_SCALE_M, rotation: CLOUD_REFLECT_ROTATION, altitude: CLOUD_REFLECT_ALTITUDE_M, strength: 0.8 };
  if (body === 'far') out.farMode = { nearSeaDiscard: true };
  if (lakeLike && look.calm && body === 'lake') out.calm = { ...LAKE_CALM };
  if (look.bed && body !== 'far') {
    out.analyticBed = body === 'sea' && opts.bedTexture && opts.planeSize
      ? { albedoTexture: opts.bedTexture, planeSize: opts.planeSize, ambient: 0.35 }
      : { albedoColor: body === 'lake' ? LAKE_BED_RGB : RIVER_BED_RGB, ambient: 0.3, rippleScale: body === 'river' ? 0.6 : 1 };
  }
  return out;
}

// ── R5: the far sea's material (one per session, the far tier dresses its sea group with it) ──

/** Where the near sea's displacement fades to flat (m from its centre) — before its 1,536 m rim, where the far sea takes over. */
export const NEAR_SEA_FADE = { start: 960, end: 1440 };
/** The near sea's half-footprint (AtlasWaterModule FOOTPRINT / 2) and the carve margin the far sheet keeps inside it. */
export const NEAR_SEA_HALF_M = 1536;
export const FAR_SEA_CARVE_MARGIN_M = 24;

interface FarSeaState { handles: RealisticWaterHandles; sig: string }
let farSea: FarSeaState | null = null;

/** The far sea's material for the current look, or null when the tier keeps the lit standard sheet. Rebuilt when the look changes. */
export function farSeaHandles(look: WaterLook, waves: GerstnerWave[] | null): RealisticWaterHandles | null {
  if (!look.farSea) return null;
  const sig = waterLookSignature({ waterSkyReflection: look.sky, waterCloudReflection: look.clouds, waterSpectrumNormals: look.spectrum, waterScattering: look.scattering, waterGlitterFilter: look.glitter, waterAnalyticBed: false, waterCalmLakes: false, waterFarSea: true, skyQuality: 'full', cloudsEnabled: true } as Partial<TierPreset>);
  if (farSea && farSea.sig === sig) return farSea.handles;
  const maps = waterNormalMaps(look.spectrum);
  const w = waves ?? createWaveSet({ amplitude: 0.4, wavelength: 22, count: 8, steepness: 0.5, seed: 41 });
  const handles = createRealisticWaterMaterial({
    ...DEFAULT_REALISTIC_WATER,
    normalMap: maps.choppy,
    screenSpaceRefraction: false,
    reflection: false,
    skyColor: 0x87a8c3,
    seaState: true,
    ...waterLookConfig('far', look, maps),
  }, w);
  handles.material.name = 'FarSeaWater';
  handles.material.userData.shared = true; // the far tier's levels never dispose it
  const old = farSea;
  farSea = { handles, sig };
  if (old) old.handles.material.userData.retired = true;
  return handles;
}

/** The current far sea handles (the water module drives their uniforms), or null. */
export function currentFarSea(): RealisticWaterHandles | null { return farSea?.handles ?? null; }

// ── R15: the planar reflection budget ───────────────────────────────────────

export interface PlanarCandidate { name: string; distance: number; visible: boolean }

/**
 * The ONE body whose planar mirror renders this frame: the visible body whose
 * water lies nearest the camera (the player is at it or looking across it).
 * Hysteresis: the current body keeps the mirror until another is 25 % nearer.
 */
export function pickPlanarBody(cands: ReadonlyArray<PlanarCandidate>, current: string | null): string | null {
  let best: PlanarCandidate | null = null;
  for (const c of cands) if (c.visible && Number.isFinite(c.distance) && (!best || c.distance < best.distance)) best = c;
  if (!best) return null;
  const cur = current ? cands.find((c) => c.name === current && c.visible && Number.isFinite(c.distance)) : undefined;
  if (cur && cur.name !== best.name && best.distance > cur.distance * 0.75) return cur.name;
  return best.name;
}

// ── R12: the medium's glow ──────────────────────────────────────────────────

/**
 * In-scattered radiance of the water around a camera `camDepth` metres down
 * (linear HDR per channel): σₛ/σₜ × the sun (and sky) reaching that depth —
 * a sunlit shallow sea glows blue-green, the deep and the night go dark.
 */
export function underwaterInscatter(sunY: number, illum: number, camDepth: number, out: THREE.Vector3): THREE.Vector3 {
  const alb = scatterAlbedo();
  const down = transmittance(Math.max(0, camDepth) + 1.5);
  const sun = Math.max(0, sunY) * 1.1 + 0.25; // the direct sun + the sky dome
  const k = 0.55 * illum * sun;
  return out.set(alb[0] * down[0] * k, alb[1] * down[1] * k, alb[2] * down[2] * k);
}

/** The noon shape the elevation rule above peaks at (1.1 · sin 53° + 0.25) — the physical in-scatter keeps noon's level. */
export const INSCATTER_NOON_SHAPE = 1.13;

/**
 * The in-scatter on the photometric curve (water session 3, 2026-09-26): `illum` is the water light's horizontal
 * GLOBAL factor (lib/water_lighting `waterLight`), which already carries the sun's height and the sky — so no second
 * elevation factor (the rule above multiplied its own elevation curve by one). Noon reads as before.
 */
export function underwaterInscatterLit(illum: number, camDepth: number, out: THREE.Vector3): THREE.Vector3 {
  const alb = scatterAlbedo();
  const down = transmittance(Math.max(0, camDepth) + 1.5);
  const k = 0.55 * Math.max(0, illum) * INSCATTER_NOON_SHAPE;
  return out.set(alb[0] * down[0] * k, alb[1] * down[1] * k, alb[2] * down[2] * k);
}

/** The medium's extinction (1/m) — Pope & Fry σₐ plus a coastal particle σₛ. */
export function underwaterExtinction(): [number, number, number] { return extinction(); }

// ── R14: waterfall look numbers (the rainbow and the strands) ────────────────

/** The rainbow billboard spans this multiple of the mist radius. */
export const RAINBOW_SPAN = 2.6;

// ── Per-body optics (shader gaps R1, 2026-09-29) ────────────────────────────
//
// The sea kept one set of coefficients for every body (Pope & Fry pure water + a coastal particle σₛ). Inland water
// is not the sea: a lake carries dissolved organic matter (CDOM — "yellow substance"), phytoplankton and silt, a river
// mostly silt. Each body's inherent optical properties are built here from its CONSTITUENTS with the standard
// bio-optical model, per RGB channel (650 / 550 / 450 nm):
//   absorption  a = a_w + a_g + a_ph + a_nap
//     a_w   Pope & Fry 1997 pure water (the engine's POPE_FRY_ABSORPTION — 0.34 / 0.0565 / 0.0092 m⁻¹)
//     a_g   CDOM: a_g(440)·exp(−S·(λ − 440)), S ≈ 0.014 nm⁻¹ (Ocean Optics Web Book, "Colored Dissolved Organic
//           Matter": most common S near 0.014, 0.007–0.026 observed)
//     a_ph  phytoplankton: 0.0654·Chl^0.672 at 440 nm (Bricaud et al. 1995, A = 0.0654, E = 0.328) × the mean
//           normalised spectrum's shape (≈ 0.94 at 450, 0.16 at 550, 0.32 at 650)
//     a_nap non-algal particles: a_nap(440)·exp(−0.0123·(λ − 440)) (Babin et al. 2003)
//   scattering  b = b_p(555)·(555/λ)^0.5 (+ pure water's negligible share)
//   beam extinction c = a + b — the CAMERA path through the medium (the underwater view)
//   diffuse attenuation Kd = √(a² + 0.235·a·b) (Kirk 1984 at a zenith sun, G = 0.425·μ₀ − 0.19) — the SUN path down
//     to a bed (the bed shading divides by the sun's height for the slant); scattering sends most photons on, so Kd
//     is far below c in turbid water
//   horizontal visibility of a dark target ≈ 4.8 / c(green) (Davies-Colley 1988), Secchi depth ≈ 1.7 / Kd(green)
//     (Poole & Atkins 1929)
// The references the numbers are pinned against (verify:water-body): a mesotrophic lake reads a Secchi depth of
// 2–8 m (Carlson 1977 / Vollenweider & Kerekes 1980: mean 4.2 m, range 1.5–8.1), a moderately turbid lowland river
// 0.5–3 m. THE TWO SEGMENTS LAW: the bed shading applies the SUN-to-bed attenuation (Kd), the surface refraction /
// the underwater medium the CAMERA-to-bed extinction (c) — two different paths, never the same one twice.

/** The wavelength (nm) each RGB channel stands for. */
export const RGB_NM: readonly [number, number, number] = [650, 550, 450];

export interface WaterConstituents {
  /** CDOM absorption at 440 nm (1/m) and its spectral slope (1/nm). */
  cdom440: number; cdomSlope: number;
  /** Chlorophyll-a (mg/m³). */
  chl: number;
  /** Non-algal particle absorption at 440 nm (1/m). */
  nap440: number;
  /** Particle scattering at 555 nm (1/m). */
  bp555: number;
}

export interface BodyOptics {
  /** Absorption, scattering, beam extinction c = a + b, diffuse Kd (1/m) and single-scattering albedo b/c, per RGB channel. */
  absorb: V3; scatter: V3; extinction: V3; kd: V3; albedo: V3;
  /** The σ the BED shading uses for the sun's path (Kd; the sea keeps its pre-wave σₜ — its exact look). */
  bedSigma: V3;
  /** Horizontal visibility of a dark target (m) and the Secchi depth (m), both on the green channel. */
  visibilityM: number; secchiM: number;
  /** Caustic contrast on the bed (1 = the sea's), and the light shafts' gain. */
  caustic: number; shaftGain: number;
  /** The overlay's veil (deep / shallow sRGB hex, per-channel extinction), the underwater fog colour and its density without the medium. */
  overlay: { deep: number; shallow: number; absorption: V3 };
  fogColor: number; fogDensity: number;
}
type V3 = [number, number, number];

const PHYTO_SHAPE: V3 = [0.32, 0.16, 0.94];
/** Kirk's G at a zenith sun (0.425·μ₀ − 0.19). */
export const KIRK_G = 0.235;

/** The inherent optics of a body from its constituents (see the section header). */
export function opticsFromConstituents(c: WaterConstituents): { absorb: V3; scatter: V3; extinction: V3; kd: V3; albedo: V3; visibilityM: number; secchiM: number } {
  const aw = POPE_FRY_ABSORPTION;
  const aph440 = 0.0654 * Math.pow(Math.max(0, c.chl), 0.672);
  const absorb = [0, 1, 2].map((k) => {
    const nm = RGB_NM[k];
    return aw[k] + c.cdom440 * Math.exp(-c.cdomSlope * (nm - 440)) + aph440 * PHYTO_SHAPE[k] + c.nap440 * Math.exp(-0.0123 * (nm - 440));
  }) as V3;
  const scatter = [0, 1, 2].map((k) => c.bp555 * Math.sqrt(555 / RGB_NM[k])) as V3;
  const extinction = [0, 1, 2].map((k) => absorb[k] + scatter[k]) as V3;
  const kd = [0, 1, 2].map((k) => Math.sqrt(absorb[k] * absorb[k] + KIRK_G * absorb[k] * scatter[k])) as V3;
  const albedo = [0, 1, 2].map((k) => scatter[k] / extinction[k]) as V3;
  return { absorb, scatter, extinction, kd, albedo, visibilityM: 4.8 / extinction[1], secchiM: 1.7 / kd[1] };
}

/** A mesotrophic lake: mildly humic (a_g(440) 0.6 m⁻¹ — lakes span 0.2–32 m⁻¹), Chl 6 mg/m³, a little silt (b_p 1 m⁻¹).
 *  Secchi ≈ 5.1 m, a dark target fades at ≈ 3.9 m — greener and browner than the sea (47 m). */
export const LAKE_CONSTITUENTS: WaterConstituents = { cdom440: 0.6, cdomSlope: 0.014, chl: 6, nap440: 0.06, bp555: 1.0 };
/** A moderately turbid lowland river: humic run-off (a_g(440) 1.2), suspended silt (b_p 3.4 m⁻¹ ≈ 7 g/m³ × 0.5 m²/g),
 *  Chl 3. Secchi ≈ 2.4 m, visibility ≈ 1.25 m — muddy. */
export const RIVER_CONSTITUENTS: WaterConstituents = { cdom440: 1.2, cdomSlope: 0.014, chl: 3, nap440: 0.3, bp555: 3.4 };

function bodyOptics(kind: 'lake' | 'river'): BodyOptics {
  const o = opticsFromConstituents(kind === 'lake' ? LAKE_CONSTITUENTS : RIVER_CONSTITUENTS);
  const lake = kind === 'lake';
  return {
    ...o,
    bedSigma: o.kd,
    caustic: lake ? 0.55 : 0.3,
    shaftGain: lake ? 0.55 : 0.25,
    overlay: lake
      ? { deep: 0x0d1a10, shallow: 0x3d5c3c, absorption: o.extinction.map((v) => v * 0.35) as V3 }
      : { deep: 0x1b1709, shallow: 0x6b5d3a, absorption: o.extinction.map((v) => v * 0.35) as V3 },
    fogColor: lake ? 0x0e2016 : 0x241f10,
    fogDensity: 1.978 / o.visibilityM,
  };
}

/** The sea's optics — EXACTLY what every sea path used before (its bed σ is σₜ, its fog and veil the old constants). */
function seaOptics(): BodyOptics {
  const absorb = [...POPE_FRY_ABSORPTION] as V3, scatter = [...COASTAL_SCATTERING] as V3;
  const ext = extinction();
  const kd = [0, 1, 2].map((k) => Math.sqrt(absorb[k] * absorb[k] + KIRK_G * absorb[k] * scatter[k])) as V3;
  return {
    absorb, scatter, extinction: ext, kd, albedo: scatterAlbedo(),
    bedSigma: ext,
    visibilityM: 4.8 / ext[1], secchiM: 1.7 / kd[1],
    caustic: 1, shaftGain: 1,
    overlay: { deep: 0x03212e, shallow: 0x2e8b8b, absorption: [0.35, 0.12, 0.08] },
    fogColor: 0x06222e, fogDensity: 0.045,
  };
}

/** The optics of each body kind (the far sea is the sea). */
export const OPTICS_BY_BODY: Record<'sea' | 'lake' | 'river', BodyOptics> = { sea: seaOptics(), lake: bodyOptics('lake'), river: bodyOptics('river') };

/**
 * One lake's σ under a turbidity factor k (1 = the reference constituents): pure water stays, the constituents'
 * share scales — Kd_w + k·(Kd_body − Kd_w). The bed shader evaluates the same lerp.
 */
export function turbidSigma(sigma: V3, k: number): V3 {
  const w = POPE_FRY_ABSORPTION;
  return [0, 1, 2].map((i) => w[i] + k * (sigma[i] - w[i])) as V3;
}

/**
 * The in-scattered glow of a LAKE or RIVER around a camera `camDepth` down (the sea keeps `underwaterInscatterLit`):
 * the body's albedo × the light reaching that depth along Kd (its diffuse path) × the noon shape.
 */
export function underwaterInscatterBody(o: BodyOptics, illum: number, camDepth: number, out: THREE.Vector3, k = 1): THREE.Vector3 {
  const kd = turbidSigma(o.kd, k);
  const d = Math.max(0, camDepth) + 1.5;
  const s = 0.55 * Math.max(0, illum) * INSCATTER_NOON_SHAPE;
  return out.set(o.albedo[0] * Math.exp(-kd[0] * d) * s, o.albedo[1] * Math.exp(-kd[1] * d) * s, o.albedo[2] * Math.exp(-kd[2] * d) * s);
}
